export default {
  columns: {
    date: 'Date',
    user: 'User',
    event: 'Event',
    description: 'Description',
  },
  tabs: {
    details: 'Request details',
    vendorDetails: 'Vendor details',
    siteDetails: 'Site details',
    projectDetails: 'Project details',
    activityLog: 'Activity Log',
  },
  // Human-readable labels for every ActivityLogAction value (FE-SDD §9).
  events: {
    CREATED: 'Request created',
    STATUS_CHANGE: 'Status change',
    VALUE_CHANGE: 'Value change',
    ENTITY_LINKED: 'Entity linked',
    VARIATION_REVIEW: 'Variation review',
    NOTE_ADDED: 'Note added',
    DOCUMENT_UPLOAD: 'Document uploaded',
    DOCUMENT_REMOVED: 'Document removed',
    COMPLIANCE_STATUS_CHANGE: 'Compliance change',
    UPDATED: 'Details updated',
    VALUE_CONTRA_ADDED: 'Value contra added',
    VALUE_CONTRA_UPDATED: 'Value contra updated',
    VALUE_CONTRA_REMOVED: 'Value contra removed',
    ITEM_CONTRA_ADDED: 'Item contra added',
    ITEM_CONTRA_UPDATED: 'Item contra updated',
    ITEM_CONTRA_REMOVED: 'Item contra removed',
    RETENTION_ADDED: 'Retention added',
    RETENTION_UPDATED: 'Retention updated',
    RETENTION_REMOVED: 'Retention removed',
    DELIVERY_LOGGED: 'Delivery logged',
    RETURN_ISSUED: 'Return issued',
    DATES_CHANGED: 'Dates changed',
    ARCHIVED: 'Archived',
    UNARCHIVED: 'Unarchived',
    BANK_DETAILS_CHANGED: 'Banking details changed',
    DELETED: 'Deleted',
    UNSYNCED: 'Unsynced',
    REACTIVATED: 'Re-synced',
    MEMBER_ADDED: 'Member added',
    MEMBER_REMOVED: 'Member removed',
    MEMBER_ROLE_CHANGED: 'Role changed',
    AMOUNT_PAID_CHANGE: 'Amount paid changed',
  },
  // Replaces events.DOCUMENT_UPLOAD for a bulk order's vendor PO share, which the API logs as the
  // PDF it sent (PE-1348).
  bulkOrderVendorPoShared: 'Vendor PO shared',
  // Replaces events.CREATED for a VENDOR, SITE or PROJECT create only, so a parent's
  // "Request created" is untouched.
  createdByEntity: {
    VENDOR_CUSTOM: 'Vendor created',
    VENDOR_SYNC: 'Vendor synced',
    SITE: 'Site created',
    PROJECT: 'Project created',
  },
  // Rendered via <i18n-t>; {oldStatus}/{newStatus} are the coloured Tag slots.
  statusChange: 'The status was updated from {oldStatus} to {newStatus}',
  // A site's or project's own status change. {orderNumber} / {bulkOrderNumber} are plain-text slots
  // until the linked-entity links land (M3); none of these says the approval happened (HLD ADR-010).
  statusChangeSite: 'The site status was updated from {oldStatus} to {newStatus}',
  statusChangeSiteByOrder:
    'The site status was updated from {oldStatus} to {newStatus} by order {orderNumber}',
  statusChangeSiteByClaim:
    'The site status was updated from {oldStatus} to {newStatus} by claim {orderNumber}',
  statusChangeSiteByBulkOrder:
    'The site status was updated from {oldStatus} to {newStatus} to approve bulk order {bulkOrderNumber}',
  statusChangeProject: 'The project status was updated from {oldStatus} to {newStatus}',
  // A site BOQ's stage change. It names nobody: the person is the row's actor (PE-1260).
  statusChangeBoq: 'The BOQ status was updated from {oldStatus} to {newStatus}',
  empty: 'No activity log entries found.',
  // Compact in-card EmptyState (Panel — PE-504) when an entity has zero entries.
  emptyTitle: 'No activity yet',
  emptyDescription: 'Actions taken on this record will appear here.',
  // Error state (Panel) — Message severity="error" + retry.
  errorMessage: 'Failed to load activity log. Please try again.',
  retry: 'Try again',
  // Pre-release cutoff banner (Panel) — Message severity="warn". Dateless: the
  // API sends only an opaque boolean, no release date (FE-SDD §2.6).
  preReleaseBanner:
    'Activity logging began after this record was created, so earlier actions are not recorded.',
  readMore: 'Read more',
  readLess: 'Read less',
  copyDescription: 'Copy description',
  copied: 'Copied',
  // Value-change detail (PE-550) — shown on hover on the correlated visible row.
  /**
  A payment's movement of an amount paid (PE-1171), every figure incl VAT (DL-248).
  */
  amountPaid: {
    viewDetails: 'View payment details',
    label: 'Payment',
    payment: 'This payment (incl VAT)',
    amountPaid: 'Amount paid (incl VAT)',
  },
  valueChange: {
    viewDetails: 'View value change details',
    total: 'Order total',
    // Credit-Request completion variant (PE-500) — a received amount, not an old→new movement.
    received: 'Amount received',
    item: 'Item',
    quantity: 'Qty',
    rate: 'Rate',
    itemTotal: 'Total',
    change: 'Change',
    // Rate-varied delivery lines that drove a linked-PR value move (PE-712).
    rateChanges: 'Rate changes',
  },
  // Note (PE-391): a NOTE_ADDED folded onto its STATUS_CHANGE row, shown on hover. Any hover that
  // holds a note is labelled "View details", whatever else it holds (DL-88).
  note: {
    viewDetails: 'View details',
    label: 'Note',
  },
  // UPDATED / COMPLIANCE_STATUS_CHANGE — the detail list moved off the row onto the hover (the row
  // shows the lead): payment-detail changes, or the missing documents behind a compliance override.
  summary: {
    viewDetails: 'View details',
    label: 'Details',
  },
  // UPDATED / BANK_DETAILS_CHANGED on a vendor, site or project: label | old → new on hover. A key
  // with no label below reads as its humanised form.
  changes: {
    viewDetails: 'View changes',
    label: 'Changes',
    to: 'to',
    none: 'None',
    sameLastFour: '(changed, same last four digits)',
  },
  // Field labels for `metadata.changes`, keyed by entity type then the backend's field path
  // (nested, so a dotted key such as `paymentDetails.vatNumber` resolves).
  fields: {
    VENDOR: {
      name: 'Vendor name',
      description: 'Vendor description',
      addressLine1: 'Address line 1',
      suburb: 'Suburb',
      city: 'City',
      country: 'Country',
      contactName: 'Sales rep name',
      contactEmail: 'Sales rep email',
      accountEmail: 'Vendor account email',
      contactNumberCountryCode: 'Sales rep number country code',
      contactNumber: 'Sales rep number',
      landlineNumber: 'Vendor landline number',
      accountNumber: 'Vendor account number',
      paymentDetails: {
        vatNumber: 'Vendor VAT number',
        vatRegistered: 'VAT registered',
        codSupported: 'Cash on Delivery',
        accountSupported: 'Account payments',
        accountType: 'Account payment period',
        bankName: 'Bank',
        branchCode: 'Branch code',
        bankAccountNumber: 'Bank account number',
      },
    },
    // Context: the same labels the backend's UPDATED descriptions use (DL-49). The hover has no
    // section headers, so each address, funding and client label names its section itself.
    SITE: {
      name: 'Site name',
      siteShortCode: 'Site short code',
      erfNumber: 'Site ERF number',
      targetMargin: 'Target profit margin',
      defaultMarkup: 'Default contract markup',
      clientInfo: {
        clientType: 'Client type',
        clientName: 'Client name',
        companyName: 'Company name',
        companyVat: 'Company VAT number',
        email: 'Client email',
        contactNumber: 'Client contact number',
        contactNumberCountryCode: 'Client contact number country code',
      },
      additionalInfo: {
        address: {
          line1: 'Site address line 1',
          line2: 'Site address suburb',
          line3: 'Site address unit or complex number',
          city: 'Site address city',
          stateProvince: 'Site address province',
          postalCode: 'Site address postal code',
          country: 'Site address country',
        },
        deliveryAddress: {
          line1: 'Delivery address line 1',
          line2: 'Delivery address suburb',
          line3: 'Delivery address unit or complex number',
          city: 'Delivery address city',
          stateProvince: 'Delivery address province',
          postalCode: 'Delivery address postal code',
          country: 'Delivery address country',
        },
      },
      fundingInfo: {
        bankFundingInfo: {
          bankId: 'Bank',
          amount: 'Loan amount',
          accountNumber: 'Loan account number',
          additionalDetails: 'Loan additional details',
        },
        selfFundingInfo: {
          amount: 'Self funding amount',
        },
      },
    },
    PROJECT: {
      name: 'Project name',
    },
  },
  // The site orders one bulk order entry moved, listed as links (PE-1124).
  childOrders: {
    label: 'Site orders ({count})',
    order: 'Order',
    site: 'Site',
    viewDetails: 'View site orders',
  },
  // Delivery breakdown (PE-550) — the per-line detail + BE-provided delivered total.
  delivery: {
    viewDetails: 'View delivered items',
    label: 'Delivered items',
    total: 'Delivered total',
    item: 'Item',
    quantity: 'Qty',
    rate: 'Rate',
    amount: 'Amount',
  },
  // The roles a site or project removal took (PE-1225, PE-1246). The row names only the person; the
  // roles follow this label on hover, on the mobile card's second line, and in the copied text.
  memberRemoval: {
    label: 'Roles removed',
    viewDetails: 'View roles removed',
  },
  // A site's DATES_CHANGED row (PE-1246). The dates are SAST calendar days; {orderNumber} and
  // {bulkOrderNumber} are plain-text slots until the linked-entity links land (M3).
  siteDates: {
    changed: 'Site dates changed to {startDate} - {endDate}',
    extendedByOrder: 'Site dates extended to {date} to include order {orderNumber}',
    extendedByClaim: 'Site dates extended to {date} to include claim {orderNumber}',
    extendedForBulkOrder: 'Site dates extended to {date} for bulk order {bulkOrderNumber}',
    extendedToApproveBulkOrder:
      'Site dates extended to {date} to approve bulk order {bulkOrderNumber}',
    startDate: 'Start date',
    endDate: 'End date',
  },
  // Membership rows on a site or project log (PE-1246). {name} is bold and each role slot is the
  // app's role Tag; the copied text reads the same with the role names as words. A removal names no
  // role: its roles follow `memberRemoval.label`.
  member: {
    added: 'Added {name} as {role}',
    invited: 'Invited {name} as {role}',
    addedBySiteInvite: 'Added {name} as {role} through a site invite',
    invitedBySiteInvite: 'Invited {name} as {role} through a site invite',
    roleChanged: "Changed {name}'s role from {oldRole} to {newRole}",
    removed: 'Removed {name}',
    removedWithProject: 'Removed {name} when they were removed from the project',
    removedWithOrganisation: 'Removed {name} when they were removed from the organisation',
    removedWithLastSiteRole: 'Removed {name} when their last site role was removed',
  },
};
